import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { canUseAnalytics } from '@/lib/cookies';
import { loadGAScript, trackPageView, trackEvent, removeGA } from '@/lib/analytics';

export const useGoogleAnalytics = () => {
  const location = useLocation();

  // Initialize Google Analytics when analytics cookies are accepted
  useEffect(() => {
    const initializeAnalytics = async () => {
      if (canUseAnalytics()) {
        try {
          await loadGAScript();
          console.log('Google Analytics initialized');
        } catch (error) {
          console.error('Failed to initialize Google Analytics:', error);
        }
      } else {
        // Remove GA if analytics cookies are not accepted
        removeGA();
      }
    };

    initializeAnalytics();
  }, []);

  // Track page views on route changes
  useEffect(() => {
    if (canUseAnalytics()) {
      trackPageView(location.pathname + location.search, document.title);
    }
  }, [location]);

  // Re-initialize or remove GA when cookie preferences change
  useEffect(() => {
    const handleStorageChange = async (e: StorageEvent) => {
      if (e.key === 'cookie-preferences') {
        if (canUseAnalytics()) {
          try {
            await loadGAScript();
          } catch (error) {
            console.error('Failed to initialize Google Analytics:', error);
          }
        } else {
          removeGA();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Return analytics functions for components to use
  return {
    trackEvent: (action: string, category: string, label?: string, value?: number) => {
      if (canUseAnalytics()) {
        trackEvent(action, category, label, value);
      }
    },
    isAnalyticsEnabled: () => canUseAnalytics(),
  };
};